import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Brain, Trash2, Plus, Tag } from 'lucide-react';

const CATEGORY_COLOR = {
  preference: 'text-bx-cyan border-bx-cyan/30 bg-bx-cyan/10',
  person: 'text-bx-magenta border-bx-magenta/30 bg-bx-magenta/10',
  event: 'text-bx-gold border-bx-gold/30 bg-bx-gold/10',
  feeling: 'text-pink-400 border-pink-400/30 bg-pink-400/10',
  goal: 'text-bx-volt border-bx-volt/30 bg-bx-volt/10',
  general: 'text-white/60 border-white/20 bg-white/5',
};

export default function MemoryPanel({ bubbleId }) {
  const [memories, setMemories] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newFact, setNewFact] = useState('');
  const [newCat, setNewCat] = useState('general');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/bubbles/${bubbleId}/memories`);
      setMemories(data || []);
    } catch (_) {}
  };

  useEffect(() => { load(); }, [bubbleId]); // eslint-disable-line

  const del = async (id) => {
    try {
      await api.delete(`/memories/${id}`);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (_) { toast.error('could not delete'); }
  };

  const add = async () => {
    if (!newFact.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bubbles/${bubbleId}/memories`, { fact: newFact.trim(), category: newCat });
      setMemories((prev) => [data, ...prev]);
      setNewFact('');
      setAdding(false);
      toast.success('Memory saved');
    } catch (_) { toast.error('could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/8" style={{ background: 'rgba(10,12,30,0.7)' }}
         data-testid="memory-panel">
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-bx-cyan" />
          <span className="text-sm font-bold text-white">What I Remember</span>
          {memories.length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-bx-cyan/15 text-bx-cyan">{memories.length}</span>
          )}
        </div>
        <button onClick={() => setAdding((a) => !a)} data-testid="memory-add-btn"
                className="text-[11px] flex items-center gap-1 text-white/50 hover:text-bx-cyan transition">
          <Plus size={12} /> Add
        </button>
      </div>

      <AnimatePresence>
        {adding && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-5 py-3 border-b border-white/8 space-y-2">
              <input value={newFact} onChange={(e) => setNewFact(e.target.value)}
                     placeholder="e.g. loves hiking in the mountains"
                     data-testid="memory-fact-input"
                     maxLength={250}
                     className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-bx-cyan" />
              <div className="flex items-center gap-2">
                <Tag size={12} className="text-white/40 shrink-0" />
                <select value={newCat} onChange={(e) => setNewCat(e.target.value)}
                        data-testid="memory-cat-select"
                        className="flex-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-white/80 outline-none">
                  {Object.keys(CATEGORY_COLOR).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button onClick={add} disabled={busy || !newFact.trim()}
                        data-testid="memory-save-btn"
                        className="px-3 py-1 rounded-full bg-bx-cyan text-black text-xs font-bold disabled:opacity-40">
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="divide-y divide-white/5" data-testid="memory-list">
        {memories.length === 0 ? (
          <div className="py-8 text-center text-white/40 text-sm">
            nothing remembered yet — start a conversation
          </div>
        ) : (
          memories.map((m) => (
            <motion.div key={m.id} layout
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        className="px-5 py-3 flex items-start gap-3 group" data-testid={`memory-item-${m.id}`}>
              <span className={`mt-0.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${CATEGORY_COLOR[m.category] || CATEGORY_COLOR.general}`}>
                {m.category}
              </span>
              <span className="flex-1 text-sm text-white/80 leading-snug">{m.fact}</span>
              <button onClick={() => del(m.id)} data-testid={`memory-delete-${m.id}`}
                      className="opacity-0 group-hover:opacity-100 transition text-white/30 hover:text-red-400">
                <Trash2 size={13} />
              </button>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
